import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useSearchParams } from "react-router-dom";
import { useSession } from "./state/session";
import { LoginPage } from "./pages/LoginPage";
import { AppShell } from "./components/AppShell";
import { homeFor } from "./lib/homeFor";
import { isSafeShareNext, resolveSharePath } from "./lib/shareLink";

const ResidentDashboard = lazy(() =>
  import("./pages/resident/ResidentDashboard").then((m) => ({ default: m.ResidentDashboard }))
);
const FrequentVisitorDashboard = lazy(() =>
  import("./pages/resident/FrequentVisitorDashboard").then((m) => ({ default: m.FrequentVisitorDashboard }))
);
const ResidentAssembliesList = lazy(() =>
  import("./pages/resident/AssembliesList").then((m) => ({ default: m.AssembliesList }))
);
const ResidentAssemblyDetail = lazy(() =>
  import("./pages/resident/AssemblyDetail").then((m) => ({ default: m.AssemblyDetail }))
);
const ResidentCommunicationsList = lazy(() =>
  import("./pages/resident/CommunicationsList").then((m) => ({ default: m.CommunicationsList }))
);
const ResidentCommunicationDetail = lazy(() =>
  import("./pages/resident/CommunicationDetail").then((m) => ({ default: m.CommunicationDetail }))
);
const ResidentNotificationsCenter = lazy(() =>
  import("./pages/resident/NotificationsCenter").then((m) => ({ default: m.NotificationsCenter }))
);
const ResidentSettingsPage = lazy(() =>
  import("./pages/resident/SettingsPage").then((m) => ({ default: m.SettingsPage }))
);
const GuardSurface = lazy(() =>
  import("./pages/guard/GuardSurface").then((m) => ({ default: m.GuardSurface }))
);
const AdminDashboard = lazy(() =>
  import("./pages/admin/AdminDashboard").then((m) => ({ default: m.AdminDashboard }))
);
const AdminAssembliesList = lazy(() =>
  import("./pages/admin/AssembliesList").then((m) => ({ default: m.AssembliesList }))
);
const AdminAssemblyForm = lazy(() =>
  import("./pages/admin/AssemblyForm").then((m) => ({ default: m.AssemblyForm }))
);
const AdminAssemblyDetail = lazy(() =>
  import("./pages/admin/AssemblyDetail").then((m) => ({ default: m.AssemblyDetail }))
);
const AdminCommunicationsList = lazy(() =>
  import("./pages/admin/CommunicationsList").then((m) => ({ default: m.CommunicationsList }))
);
const AdminCommunicationForm = lazy(() =>
  import("./pages/admin/CommunicationForm").then((m) => ({ default: m.CommunicationForm }))
);
const AdminCommunicationDetail = lazy(() =>
  import("./pages/admin/CommunicationDetail").then((m) => ({ default: m.CommunicationDetail }))
);
const AdminUsers = lazy(() =>
  import("./pages/admin/AdminUsers").then((m) => ({ default: m.AdminUsers }))
);
const AdminVisitors = lazy(() =>
  import("./pages/admin/AdminVisitors").then((m) => ({ default: m.AdminVisitors }))
);
const PlatformDashboard = lazy(() =>
  import("./pages/platform/PlatformDashboard").then((m) => ({ default: m.PlatformDashboard }))
);
const PlatformContactMessages = lazy(() =>
  import("./pages/platform/ContactMessagesList").then((m) => ({ default: m.ContactMessagesList }))
);
const PlatformContactMessageDetail = lazy(() =>
  import("./pages/platform/ContactMessageDetail").then((m) => ({ default: m.ContactMessageDetail }))
);
const PlatformBillingHub = lazy(() =>
  import("./pages/platform/PlatformBilling").then((m) => ({ default: m.PlatformBillingHub }))
);
const PlatformBillingAccountDetail = lazy(() =>
  import("./pages/platform/PlatformBilling").then((m) => ({ default: m.PlatformBillingAccountDetail }))
);
const PlatformBillingBanks = lazy(() =>
  import("./pages/platform/PlatformBilling").then((m) => ({ default: m.PlatformBillingBanks }))
);
const PlatformPaymentReviewDetail = lazy(() =>
  import("./pages/platform/PlatformBilling").then((m) => ({ default: m.PlatformPaymentReviewDetail }))
);
const AdminSubscriptionPage = lazy(() =>
  import("./pages/admin/AdminSubscriptionPage").then((m) => ({ default: m.AdminSubscriptionPage }))
);
const VisitorAccessPage = lazy(() =>
  import("./pages/visitor/VisitorAccessPage").then((m) => ({ default: m.VisitorAccessPage }))
);
const EventJoinPage = lazy(() =>
  import("./pages/visitor/EventJoinPage").then((m) => ({ default: m.EventJoinPage }))
);
const ActivateInvitePage = lazy(() =>
  import("./pages/ActivateInvitePage").then((m) => ({ default: m.ActivateInvitePage }))
);
const ForgotPasswordPage = lazy(() =>
  import("./pages/ForgotPasswordPage").then((m) => ({ default: m.ForgotPasswordPage }))
);
const ResetPasswordPage = lazy(() =>
  import("./pages/ResetPasswordPage").then((m) => ({ default: m.ResetPasswordPage }))
);
const ResidentBillingDashboard = lazy(() =>
  import("./pages/resident/billing/FinancialDashboard").then((m) => ({ default: m.FinancialDashboard }))
);
const ResidentOutstandingCharges = lazy(() =>
  import("./pages/resident/billing/OutstandingCharges").then((m) => ({ default: m.OutstandingCharges }))
);
const ResidentChargeDetail = lazy(() =>
  import("./pages/resident/billing/ChargeDetail").then((m) => ({ default: m.ChargeDetail }))
);
const ResidentSubmitPayment = lazy(() =>
  import("./pages/resident/billing/SubmitPayment").then((m) => ({ default: m.SubmitPayment }))
);
const ResidentPaymentHistory = lazy(() =>
  import("./pages/resident/billing/PaymentHistory").then((m) => ({ default: m.PaymentHistory }))
);
const ResidentReceipts = lazy(() =>
  import("./pages/resident/billing/ReceiptsList").then((m) => ({ default: m.ReceiptsList }))
);
const LedgerList = lazy(() =>
  import("./pages/ledger/LedgerList").then((m) => ({ default: m.LedgerList }))
);
const LedgerForm = lazy(() =>
  import("./pages/ledger/LedgerForm").then((m) => ({ default: m.LedgerForm }))
);
const LedgerDetail = lazy(() =>
  import("./pages/ledger/LedgerDetail").then((m) => ({ default: m.LedgerDetail }))
);
const AdminBillingDashboard = lazy(() =>
  import("./pages/admin/billing/AdminFinancialDashboard").then((m) => ({
    default: m.AdminFinancialDashboard,
  }))
);
const AdminChargeManagement = lazy(() =>
  import("./pages/admin/billing/ChargeManagement").then((m) => ({ default: m.ChargeManagement }))
);
const AdminPaymentReviewQueue = lazy(() =>
  import("./pages/admin/billing/PaymentReviewQueue").then((m) => ({ default: m.PaymentReviewQueue }))
);
const AdminPaymentReviewDetail = lazy(() =>
  import("./pages/admin/billing/PaymentReviewQueue").then((m) => ({ default: m.PaymentReviewDetail }))
);
const AdminDelinquency = lazy(() =>
  import("./pages/admin/billing/DelinquencyDashboard").then((m) => ({ default: m.DelinquencyDashboard }))
);
const AdminBillingReports = lazy(() =>
  import("./pages/admin/billing/BillingReports").then((m) => ({ default: m.BillingReports }))
);
const AdminBankAccounts = lazy(() =>
  import("./pages/admin/billing/BankAccountsAdmin").then((m) => ({ default: m.BankAccountsAdmin }))
);
const AdminSchedules = lazy(() =>
  import("./pages/admin/billing/SchedulesAdmin").then((m) => ({ default: m.SchedulesAdmin }))
);
const ResidentReservationsCalendar = lazy(() =>
  import("./pages/resident/ReservationsCalendar").then((m) => ({ default: m.ReservationsCalendar }))
);
const ResidentMyReservations = lazy(() =>
  import("./pages/resident/MyReservations").then((m) => ({ default: m.MyReservations }))
);
const AdminReservations = lazy(() =>
  import("./pages/admin/reservations/AdminReservations").then((m) => ({ default: m.AdminReservations }))
);
const AdminCommonAreaForm = lazy(() =>
  import("./pages/admin/reservations/CommonAreaForm").then((m) => ({ default: m.CommonAreaForm }))
);
const AdminReservationQueue = lazy(() =>
  import("./pages/admin/reservations/ReservationQueue").then((m) => ({ default: m.ReservationQueue }))
);

// Native app only (VITE_PLATFORM=native). Inline env check so the web build drops the chunk.
const NativeLoginPage =
  import.meta.env.VITE_PLATFORM === "native"
    ? lazy(() => import("./native/NativeLoginPage").then((m) => ({ default: m.NativeLoginPage })))
    : null;

const RESIDENT_ROLES = ["resident", "frequent_visitor"] as const;

export default function App() {
  return (
    <Routes>
      <Route
        path="/access/:token"
        element={
          <Suspense fallback={<FullLoader label="Cargando acceso…" />}>
            <VisitorAccessPage />
          </Suspense>
        }
      />

      <Route
        path="/evento/:token"
        element={
          <Suspense fallback={<FullLoader label="Cargando evento…" />}>
            <EventJoinPage />
          </Suspense>
        }
      />

      <Route
        path="/activar/:token"
        element={
          <Suspense fallback={<FullLoader label="Cargando invitación…" />}>
            <ActivateInvitePage />
          </Suspense>
        }
      />

      <Route
        path="/recuperar"
        element={
          <Suspense fallback={<FullLoader label="Cargando…" />}>
            <ForgotPasswordPage />
          </Suspense>
        }
      />

      <Route
        path="/recuperar/:token"
        element={
          <Suspense fallback={<FullLoader label="Cargando enlace…" />}>
            <ResetPasswordPage />
          </Suspense>
        }
      />

      <Route path="/login" element={<LoginRoute />} />

      {/* Layout route + Outlet — avoids nested <Routes> under /* which blank after login redirect */}
      <Route element={<AuthLayout />}>
        <Route
          index
          element={
            <Allow roles={[...RESIDENT_ROLES]}>
              <AccessHome />
            </Allow>
          }
        />
        <Route
          path="asambleas"
          element={
            <Allow roles={[...RESIDENT_ROLES]}>
              <ResidentAssembliesList />
            </Allow>
          }
        />
        <Route
          path="asambleas/:id"
          element={
            <Allow roles={[...RESIDENT_ROLES]}>
              <ResidentAssemblyDetail />
            </Allow>
          }
        />
        <Route path="ir/asambleas/:id" element={<ShareLanding />} />
        <Route path="ir/cuentas" element={<ShareLanding />} />
        <Route
          path="comunicaciones"
          element={
            <Allow roles={[...RESIDENT_ROLES]}>
              <ResidentCommunicationsList />
            </Allow>
          }
        />
        <Route
          path="comunicaciones/:id"
          element={
            <Allow roles={[...RESIDENT_ROLES]}>
              <ResidentCommunicationDetail />
            </Allow>
          }
        />
        <Route
          path="reservas"
          element={
            <Allow roles={["resident"]}>
              <ResidentReservationsCalendar />
            </Allow>
          }
        />
        <Route
          path="reservas/mias"
          element={
            <Allow roles={["resident"]}>
              <ResidentMyReservations />
            </Allow>
          }
        />
        <Route path="notificaciones" element={<ResidentNotificationsCenter />} />
        <Route path="ajustes" element={<ResidentSettingsPage />} />
        <Route
          path="pagos"
          element={
            <Allow roles={["resident"]}>
              <ResidentBillingDashboard />
            </Allow>
          }
        />
        <Route
          path="pagos/cargos"
          element={
            <Allow roles={["resident"]}>
              <ResidentOutstandingCharges />
            </Allow>
          }
        />
        <Route
          path="pagos/cargos/:id"
          element={
            <Allow roles={["resident"]}>
              <ResidentChargeDetail />
            </Allow>
          }
        />
        <Route
          path="pagos/nuevo"
          element={
            <Allow roles={["resident"]}>
              <ResidentSubmitPayment />
            </Allow>
          }
        />
        <Route
          path="pagos/historial"
          element={
            <Allow roles={["resident"]}>
              <ResidentPaymentHistory />
            </Allow>
          }
        />
        <Route
          path="pagos/recibos"
          element={
            <Allow roles={["resident"]}>
              <ResidentReceipts />
            </Allow>
          }
        />
        <Route
          path="cuentas"
          element={
            <Allow roles={["resident"]}>
              <LedgerList basePath="/cuentas" />
            </Allow>
          }
        />
        <Route
          path="cuentas/:id"
          element={
            <Allow roles={["resident"]}>
              <LedgerDetail basePath="/cuentas" />
            </Allow>
          }
        />

        <Route
          path="guardia"
          element={
            <Allow roles={["guard"]}>
              <GuardSurface />
            </Allow>
          }
        />

        <Route
          path="admin"
          element={
            <Allow roles={["admin"]}>
              <AdminDashboard />
            </Allow>
          }
        />
        <Route
          path="admin/usuarios"
          element={
            <Allow roles={["admin"]}>
              <AdminUsers />
            </Allow>
          }
        />
        <Route
          path="admin/visitantes"
          element={
            <Allow roles={["admin"]}>
              <AdminVisitors />
            </Allow>
          }
        />
        <Route
          path="admin/asambleas"
          element={
            <Allow roles={["admin"]}>
              <AdminAssembliesList />
            </Allow>
          }
        />
        <Route
          path="admin/asambleas/nueva"
          element={
            <Allow roles={["admin"]}>
              <AdminAssemblyForm />
            </Allow>
          }
        />
        <Route
          path="admin/asambleas/:id"
          element={
            <Allow roles={["admin"]}>
              <AdminAssemblyDetail />
            </Allow>
          }
        />
        <Route
          path="admin/comunicaciones"
          element={
            <Allow roles={["admin"]}>
              <AdminCommunicationsList />
            </Allow>
          }
        />
        <Route
          path="admin/comunicaciones/nueva"
          element={
            <Allow roles={["admin"]}>
              <AdminCommunicationForm />
            </Allow>
          }
        />
        <Route
          path="admin/comunicaciones/:id"
          element={
            <Allow roles={["admin"]}>
              <AdminCommunicationDetail />
            </Allow>
          }
        />
        <Route
          path="admin/reservas"
          element={
            <Allow roles={["admin"]}>
              <AdminReservations />
            </Allow>
          }
        />
        <Route
          path="admin/reservas/cola"
          element={
            <Allow roles={["admin"]}>
              <AdminReservationQueue />
            </Allow>
          }
        />
        <Route
          path="admin/reservas/areas/nueva"
          element={
            <Allow roles={["admin"]}>
              <AdminCommonAreaForm />
            </Allow>
          }
        />
        <Route
          path="admin/reservas/areas/:id"
          element={
            <Allow roles={["admin"]}>
              <AdminCommonAreaForm />
            </Allow>
          }
        />
        <Route
          path="admin/pagos"
          element={
            <Allow roles={["admin"]}>
              <AdminBillingDashboard />
            </Allow>
          }
        />
        <Route
          path="admin/pagos/cargos"
          element={
            <Allow roles={["admin"]}>
              <AdminChargeManagement />
            </Allow>
          }
        />
        <Route
          path="admin/pagos/revision"
          element={
            <Allow roles={["admin"]}>
              <AdminPaymentReviewQueue />
            </Allow>
          }
        />
        <Route
          path="admin/pagos/revision/:id"
          element={
            <Allow roles={["admin"]}>
              <AdminPaymentReviewDetail />
            </Allow>
          }
        />
        <Route
          path="admin/pagos/morosidad"
          element={
            <Allow roles={["admin"]}>
              <AdminDelinquency />
            </Allow>
          }
        />
        <Route
          path="admin/pagos/reportes"
          element={
            <Allow roles={["admin"]}>
              <AdminBillingReports />
            </Allow>
          }
        />
        <Route
          path="admin/pagos/cuentas"
          element={
            <Allow roles={["admin"]}>
              <AdminBankAccounts />
            </Allow>
          }
        />
        <Route
          path="admin/pagos/calendarios"
          element={
            <Allow roles={["admin"]}>
              <AdminSchedules />
            </Allow>
          }
        />

        <Route
          path="admin/cuentas"
          element={
            <Allow roles={["admin"]}>
              <LedgerList basePath="/admin/cuentas" canManage />
            </Allow>
          }
        />
        <Route
          path="admin/cuentas/nueva"
          element={
            <Allow roles={["admin"]}>
              <LedgerForm />
            </Allow>
          }
        />
        <Route
          path="admin/cuentas/:id/editar"
          element={
            <Allow roles={["admin"]}>
              <LedgerForm />
            </Allow>
          }
        />
        <Route
          path="admin/cuentas/:id"
          element={
            <Allow roles={["admin"]}>
              <LedgerDetail basePath="/admin/cuentas" canManage />
            </Allow>
          }
        />

        <Route
          path="admin/suscripcion"
          element={
            <Allow roles={["admin"]}>
              <AdminSubscriptionPage />
            </Allow>
          }
        />

        <Route
          path="platform"
          element={
            <Allow roles={["platform_admin"]}>
              <PlatformDashboard />
            </Allow>
          }
        />
        <Route
          path="platform/facturacion"
          element={
            <Allow roles={["platform_admin", "billing_contact"]}>
              <PlatformBillingHub />
            </Allow>
          }
        />
        <Route
          path="platform/facturacion/bancos"
          element={
            <Allow roles={["platform_admin"]}>
              <PlatformBillingBanks />
            </Allow>
          }
        />
        <Route
          path="platform/facturacion/cuentas/:id"
          element={
            <Allow roles={["platform_admin"]}>
              <PlatformBillingAccountDetail />
            </Allow>
          }
        />
        <Route
          path="platform/facturacion/pagos/:id"
          element={
            <Allow roles={["platform_admin"]}>
              <PlatformPaymentReviewDetail />
            </Allow>
          }
        />
        <Route
          path="platform/contacto"
          element={
            <Allow roles={["platform_admin"]}>
              <PlatformContactMessages />
            </Allow>
          }
        />
        <Route
          path="platform/contacto/:id"
          element={
            <Allow roles={["platform_admin"]}>
              <PlatformContactMessageDetail />
            </Allow>
          }
        />

        <Route path="*" element={<RoleHome />} />
      </Route>
    </Routes>
  );
}

function AccessHome() {
  const { session } = useSession();
  if (session?.role === "frequent_visitor") return <FrequentVisitorDashboard />;
  return <ResidentDashboard />;
}

function loginPathFor(next: string): string {
  return isSafeShareNext(next) ? `/login?next=${encodeURIComponent(next)}` : "/login";
}

function LoginRoute() {
  const { session } = useSession();
  const [params] = useSearchParams();
  const next = params.get("next");
  if (session) {
    return <Navigate to={isSafeShareNext(next) ? next : homeFor(session.role)} replace />;
  }
  if (NativeLoginPage) {
    return (
      <Suspense fallback={<FullLoader />}>
        <NativeLoginPage />
      </Suspense>
    );
  }
  return <LoginPage />;
}

function ShareLanding() {
  const { session } = useSession();
  const location = useLocation();
  return <Navigate to={resolveSharePath(session?.role ?? "", `${location.pathname}${location.search}`)} replace />;
}

function AuthLayout() {
  const { session, loading } = useSession();
  const location = useLocation();

  if (loading) return <FullLoader />;
  if (!session) {
    return <Navigate to={loginPathFor(`${location.pathname}${location.search}`)} replace />;
  }

  return (
    <AppShell role={session.role}>
      <Suspense fallback={<FullLoader />}>
        <Outlet />
      </Suspense>
    </AppShell>
  );
}

function Allow({ roles, children }: { roles: string[]; children: ReactNode }) {
  const { session } = useSession();
  if (!session || !roles.includes(session.role)) {
    return <Navigate to={homeFor(session?.role ?? "")} replace />;
  }
  return <>{children}</>;
}

function RoleHome() {
  const { session } = useSession();
  return <Navigate to={homeFor(session?.role ?? "")} replace />;
}

function FullLoader({ label = "Cargando…" }: { label?: string }) {
  return (
    <div className="grid min-h-dvh place-items-center">
      <div className="flex flex-col items-center gap-3 text-ink-muted">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-brand-600" />
        <span className="text-sm">{label}</span>
      </div>
    </div>
  );
}
